/**
 * /dashboard/payroll/new — Create a new payroll run (G5.5,
 * POST /api/v1/payroll/runs).
 *
 * HR-admin only (PayrollRoleGate mirrors backend RBAC). RunForm handles
 * the post-create redirect to /dashboard/payroll/$id (via useNavigate
 * inside the form).
 *
 * Migration note (Phase 3e — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/payroll/new/page.tsx.
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { PayrollModuleShell } from "@/components/shared/payroll-module-shell";
import { RunForm } from "@/components/payroll/run-form";
import { usePayrollSetup } from "@/hooks/use-payroll";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";

/** ?year=&month= — "Start {month} payroll" on the payroll page pre-picks the
 *  month (switching-and-first-payroll §4). Anything else is ignored. */
function parseRunSearch(search: Record<string, unknown>): { year?: number; month?: number } {
  const year = Number(search.year);
  const month = Number(search.month);
  return Number.isInteger(year) && year >= 2020 && year <= 2100 && Number.isInteger(month) && month >= 1 && month <= 12
    ? { year, month }
    : {};
}

export const Route = createFileRoute("/_authgated/app/$tenantSlug/payroll/new")({
  validateSearch: parseRunSearch,
  component: NewPayrollRunRoute,
});

function NewPayrollRunRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { year, month } = Route.useSearch();
  // Nobody can be paid yet: point back to the guide (the form below still
  // works and keeps its inputs-missing + "run without them" fallback).
  const setup = usePayrollSetup();
  return (
    <PayrollModuleShell
      title="New Payroll Run"
      description="Pick a month. WorkRight works out pay for every active employee as a draft you can review before locking."
      actions={
        <Button asChild variant="outline" className="gap-1.5">
          <Link to="/app/$tenantSlug/payroll" params={{ tenantSlug }}>
            <ArrowLeft aria-hidden className="h-4 w-4" />
            Back
          </Link>
        </Button>
      }
    >
      {setup.data && !setup.data.ready && (
        <Alert className="mb-6 max-w-2xl" data-testid="payroll-new-not-ready">
          <AlertDescription className="text-sm">
            Payroll needs at least one person with a salary.{" "}
            <Link
              to="/app/$tenantSlug/payroll"
              params={{ tenantSlug }}
              className="font-medium text-primary underline-offset-4 hover:underline"
            >
              Open Your First Payroll
            </Link>{" "}
            to see what&apos;s left.
          </AlertDescription>
        </Alert>
      )}
      <Card className="max-w-2xl">
        <CardContent className="pt-6">
          <RunForm defaultYear={year} defaultMonth={month} />
        </CardContent>
      </Card>
    </PayrollModuleShell>
  );
}
